import axios from '@/plugins/axios';
import storeutils from '@/stores/utils';

const state = {
    entityConnectors: [],
    entityConnectorsLoading: false,
    syncStatus: null,
    syncStatusLoading: false,
    connectionTest: null,
    connectionTestLoading: false,
};

const mutations = {
    updateEntityConnectors(state, connectors) {
        state.entityConnectors = connectors;
    },
    updateEntityConnectorsLoading(state, loading) {
        state.entityConnectorsLoading = loading;
    },
    updateSyncStatus(state, status) {
        state.syncStatus = status;
    },
    updateSyncStatusLoading(state, loading) {
        state.syncStatusLoading = loading;
    },
    updateConnectionTest(state, test) {
        state.connectionTest = test;
    },
    updateConnectionTestLoading(state, loading) {
        state.connectionTestLoading = loading;
    },
};

const actions = {
    async fetchEntityConnectors({ commit }) {
        commit('updateEntityConnectorsLoading', true);
        try {
            const response = await axios.get('/api/connectors/entityconnectors/');
            commit('updateEntityConnectors', response.data.results || response.data);
        } catch (xhr_error) {
            const error = storeutils.handleError(xhr_error);
            throw error;
        } finally {
            commit('updateEntityConnectorsLoading', false);
        }
    },
    
    async fetchSyncStatus({ commit }, { type, resourceId }) {
        commit('updateSyncStatusLoading', true);
        try {
            const fieldName = type.includes('invoice') ? 'invoice' : type.slice(0, -1);
            const response = await axios.get(`/api/connectors/pennylane/sync/${type}/`, {
                params: { [fieldName]: resourceId }
            });
            const status = response.data.results?.[0] || response.data[0] || null;
            commit('updateSyncStatus', status);
            return status;
        } catch (xhr_error) {
            commit('updateSyncStatus', null);
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('updateSyncStatusLoading', false);
        }
    },
    
    async triggerSync({ dispatch }, { type, syncId, force = false }) {
        await axios.post(`/api/connectors/pennylane/sync/${type}/${syncId}/sync/`, { force });
    },
    
    async testConnection({ commit }, entityConnectorId) {
        commit('updateConnectionTestLoading', true);
        try {
            const response = await axios.post(`/api/connectors/entityconnectors/${entityConnectorId}/test-connection/`);
            commit('updateConnectionTest', response.data);
            return response.data;
        } catch (xhr_error) {
            const error = storeutils.handleError(xhr_error);
            commit('updateConnectionTest', { connected: false, error: error.message });
            throw error;
        } finally {
            commit('updateConnectionTestLoading', false);
        }
    },
    
    async updateEntityConnectorSettings({ commit, dispatch }, { id, settings }) {
        try {
            const response = await axios.patch(`/api/connectors/entityconnectors/${id}/`, { settings });
            await dispatch('fetchEntityConnectors');
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },
    
    async createEntityConnector({ commit, dispatch }, { connectorId, settings = {} }) {
        try {
            const response = await axios.post('/api/connectors/entityconnectors/', {
                connector: connectorId,
                settings: settings,
                status: 'inactive',
            });
            await dispatch('fetchEntityConnectors');
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },
    
    async fetchConnectors() {
        try {
            const response = await axios.get('/api/connectors/connectors/');
            return response.data.results || response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },
};

const getters = {
    hasPennylane: (state) => {
        return state.entityConnectors.some(ec => ec.connector_data?.code === 'pennylane');
    },
    pennylaneConnector: (state) => {
        return state.entityConnectors.find(ec => ec.connector_data?.code === 'pennylane') || null;
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

